import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { ServiceOrderRequest, postContactUsCreateServiceRequest } from "api"
import { createRef, useEffect, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { schemaCommon } from "reusable/Form/FormSchema"
import { trackLeadSubmit } from "reusable/gtm"
import { ButtonWithArrow, NewText, useIsDesktop } from "ui"
import GeneralErrorMsg from "ui/Errors/GeneralErrorMsg"
import { getSubjectId, getUtmParams } from "utils"
import * as yup from "yup"
import ContactUsState from "./ContactUsState"
import FormInput from "./FormInput"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledDiv = styled.div`
  margin-top: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 20px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-top: 28px;
  }
`
const StyledForm = styled.div`
  padding-top: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 16px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-top: 24px;
  }
`

const CaptchaStyled = styled.div`
  visibility: hidden !important;
`

const StyledButton = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
  margin-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 0px;
    margin-top: 18px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-top: 28px;
  }
`
const Asterisk = styled.span`
  color: blue;
  width: 4px;
`

const StyledTextWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 4px;
  p {
    margin-left: 8px;
  }
`

const StyledDivWrapper = styled.div`
  width: 100%;
  display: grid;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr;
    gap: 21px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
  grid-template-columns: 1fr;
`

export const StyledFiedlset = styled.fieldset`
  padding: 0;
  margin: 0;
  border: none;
`

export const StyledLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  white-space: nowrap;
`

const schema = yup.object({
  firstName: schemaCommon.firstName,
  lastName: schemaCommon.lastName,
  email: schemaCommon.email,
  phoneNumber: schemaCommon.phoneNumber,
  description: schemaCommon.description,
  comments: schemaCommon.comments,
})

interface IFormInputs {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  description: string
  comments: string
}

function CustomerService() {
  const isDesktop = useIsDesktop()
  const [isSuccess, setIsSuccess] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const reCaptchaRef = createRef<ReCAPTCHA>()
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      description: "",
      comments: "",
    },
    shouldFocusError: true,

    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    setIsLoading(true)
    try {
      reCaptchaRef?.current?.reset()
      const recaptchaResponse =
        (await reCaptchaRef?.current?.executeAsync()) as string
      const postData: ServiceOrderRequest = {
        FirstName: data.firstName,
        LastName: data.lastName,
        MobilePhone: data.phoneNumber,
        Email: data.email,
        SubjectId: getSubjectId("פז - שיווקי"),
        Message: data.description + " " + data.comments,
        IsFromYellowBox: false,
        CustomerId: "",
        City: "",
        CustomerTypeId: "",
        PlatformUserId: "",
        GRecaptchaResponse: recaptchaResponse,
        ...getUtmParams(),
        Source: "09",
      }

      const response = (await postContactUsCreateServiceRequest(
        "",
        postData,
      )) as any
      // success
      if (response?.data?.Body && !response.data.errorCode) {
        setIsSuccess(true)
        trackLeadSubmit("שירות לקוחות", data?.description)
        reset()
      } else {
        setIsSuccess(false)
      }
    } catch (error) {
      setIsSuccess(false)
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsLoading(false)
      setIsOpen(true)
    }
  }
  useEffect(() => {
    const firstErrorKey = Object.keys(errors).find((key) => key in errors) as
      | keyof IFormInputs
      | undefined

    if (firstErrorKey) {
      const errorElement = document.querySelector(
        `input[name="${firstErrorKey}"]`,
      ) as HTMLInputElement | null

      errorElement?.focus()
    }
  }, [errors])
  return (
    <StyledDiv>
      <NewText
        component={"p"}
        largeDesktopFontSize={24}
        largeDesktopLineHeight={"34px"}
        desktopFontWeight={400}
        mobileFontSize={18}
        mobileLineHeight={"24px"}
        desktopFontSize={16}
        desktopLineHeight="22px"
      >
        אנחנו בפז כאן בשבילך כדי לענות על כל שאלה ולסייע לך בכל מה שצריך.
      </NewText>
      <StyledForm>
        <form onSubmit={handleSubmit(onSubmit)}>
          <StyledFiedlset>
            <StyledLegend>טופס יצירת קשר עם שירות לקוחות</StyledLegend>

            <StyledDivWrapper>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    field={field}
                    error={errors.firstName?.message}
                    placeholder="שם פרטי"
                    type="text"
                    required={true}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    field={field}
                    error={errors.lastName?.message}
                    placeholder="שם משפחה"
                    type="text"
                    required={true}
                  />
                )}
              />
            </StyledDivWrapper>
            <StyledDivWrapper>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <FormInput
                    field={field}
                    error={errors.email?.message}
                    placeholder="אימייל"
                    type="text"
                    required={true}
                  />
                )}
              />

              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <FormInput
                    field={field}
                    error={errors.phoneNumber?.message}
                    placeholder="טלפון"
                    type="number"
                    required={true}
                  />
                )}
              />
            </StyledDivWrapper>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <FormInput
                  field={field}
                  error={errors.description?.message}
                  placeholder={
                    isDesktop ? "באיזה נושא נוכל לסייע לך?" : "נושא הפנייה"
                  }
                  type="text"
                  fullWidth
                  required={true}
                />
              )}
            />
            <Controller
              name="comments"
              control={control}
              render={({ field }) => (
                <FormInput
                  field={field}
                  placeholder={
                    isDesktop ? "יש לך שאלה או בקשה?" : "איך אפשר לעזור?"
                  }
                  type="text"
                  fullWidth
                />
              )}
            />
            <StyledTextWrapper>
              <Asterisk>*</Asterisk>
              <NewText
                component={"p"}
                largeDesktopFontSize={16}
                largeDesktopLineHeight={"24px"}
                desktopFontWeight={400}
                desktopFontSize={12}
                desktopLineHeight="16px"
              >
                שדות המסומנים בכוכב הם שדות חובה
              </NewText>
            </StyledTextWrapper>
          </StyledFiedlset>
          {Object.keys(errors).length > 0 && (
            <GeneralErrorMsg error={"אחד או יותר מהשדות בטופס אינם תקינים"} />
          )}
          <StyledButton>
            <ButtonWithArrow
              disabled={isLoading}
              title={"שליחה"}
              largeDesktopFontSize={20}
              justifyContent="center"
              type="submit"
              desktopFontSize={14}
              smallDesktopHeight={30}
              smallDesktopWidth={106}
              desktopWidth={156}
              desktopHeight={44}
              svgRelative
            />
          </StyledButton>
          <CaptchaStyled>
            {RECAPTCHA_KEY && (
              <ReCAPTCHA
                ref={reCaptchaRef}
                sitekey={RECAPTCHA_KEY}
                size="invisible"
                hl="iw"
                tabIndex={-1}
              />
            )}
          </CaptchaStyled>
        </form>
      </StyledForm>
      <ContactUsState
        isOpen={isOpen}
        isSuccess={isSuccess}
        setIsOpen={setIsOpen}
      />
    </StyledDiv>
  )
}

export default CustomerService
